import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Page, PageHeader } from '../components/layout/AppShell';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { AsyncBoundary, EmptyState, Skeleton } from '../components/ui/AsyncBoundary';
import { StatTile } from '../components/charts/StatTile';
import { StackedBar, BarList } from '../components/charts/StackedBar';
import { FleetMap } from '../components/map/FleetMap';
import { DataTable } from '../components/ui/DataTable';
import { AlertItem } from '../components/domain/VehicleBits';
import { vehicleColumns } from '../components/domain/columns';
import { useResource } from '../hooks/useResource';
import { useFleetLive } from '../hooks/useFleetLive';
import { useAuth } from '../auth/AuthProvider';
import { VEHICLE_STATES, VEHICLE_STATE_ORDER, HEALTH_BANDS } from '../lib/domain';
import * as fmt from '../lib/format';

/**
 * The dashboard.
 *
 * Ordered by what someone glances at first: the five numbers, then where
 * everything is, then what needs attention. The map is the largest element on
 * the page because "where are my vehicles" is the question this product exists
 * to answer.
 *
 * Every panel here is scoped by the server — a manager's dashboard is their
 * fleet's dashboard, with no client-side filtering involved.
 */
export function Dashboard() {
  const navigate = useNavigate();
  const { user, scope } = useAuth();
  const [selectedId, setSelectedId] = useState(null);

  // The snapshot refreshes quietly; live socket events fill the gaps between.
  const summary = useResource('/api/analytics/fleet', null, { pollMs: 30_000 });
  const vehiclesResource = useResource('/api/vehicles', { limit: 500 }, { pollMs: 60_000 });
  const alerts = useResource('/api/alerts', { limit: 6, state: 'OPEN' }, { pollMs: 30_000 });
  const health = useResource('/api/analytics/health', null, { pollMs: 120_000 });

  const { vehicles } = useFleetLive(vehiclesResource.data?.vehicles);

  const scopeLabel = scope?.allOrganizations
    ? 'All organisations'
    : scope?.fleets?.length
      ? scope.fleets.map((f) => f.name).join(', ')
      : scope?.vehicleCount !== null && scope?.vehicleCount !== undefined
        ? `${scope.vehicleCount} assigned vehicle${scope.vehicleCount === 1 ? '' : 's'}`
        : 'Your organisation';

  return (
    <Page className="space-y-4">
      <PageHeader
        title={`Good ${partOfDay()}, ${user?.fullName?.split(' ')[0] || 'there'}`}
        subtitle={scopeLabel}
        actions={
          <Button
            variant="secondary"
            size="sm"
            icon="refresh"
            onClick={() => {
              summary.refetch();
              vehiclesResource.refetch();
              alerts.refetch();
            }}
          >
            Refresh
          </Button>
        }
      />

      <KpiRow summary={summary} vehicles={vehicles} />

      <div className="grid items-start gap-4 xl:grid-cols-[1.65fr_1fr]">
        <Card className="overflow-hidden">
          <CardHeader
            title="Live fleet"
            subtitle={`${vehicles.length} vehicle${vehicles.length === 1 ? '' : 's'} in view`}
            icon="location"
            actions={
              <Button variant="ghost" size="sm" iconRight="chevronRight" onClick={() => navigate('/vehicles')}>
                All vehicles
              </Button>
            }
          />
          <div className="p-3">
            <FleetMap
              vehicles={vehicles}
              selectedId={selectedId}
              onSelect={setSelectedId}
              followSelected
              height={430}
            />
          </div>
        </Card>

        <div className="space-y-4">
          <FleetStatusCard summary={summary} vehicles={vehicles} />
          <AlertsCard alerts={alerts} />
        </div>
      </div>

      <div className="grid items-start gap-4 xl:grid-cols-[1.65fr_1fr]">
        <Card className="overflow-hidden">
          <CardHeader
            title="Needs attention first"
            subtitle="Offline devices, then the lowest health scores"
            icon="truck"
          />
          <AsyncBoundary
            state={vehiclesResource}
            skeleton={<Skeleton className="m-4 h-48" />}
            isEmpty={(data) => !data.vehicles.length}
            empty={<EmptyState icon="truck" title="No vehicles yet" description="Add a vehicle to get started." />}
          >
            {() => (
              <DataTable
                dense
                columns={vehicleColumns({ compact: true })}
                rows={triageOrder(vehicles).slice(0, 8)}
                onRowClick={(row) => navigate(`/vehicles/${row.id}`)}
              />
            )}
          </AsyncBoundary>
        </Card>

        <HealthCard health={health} />
      </div>
    </Page>
  );
}

/* ------------------------------------------------------------------ parts -- */

function KpiRow({ summary, vehicles }) {
  // Counted from the live-merged list rather than the polled snapshot, so the
  // tiles move with the map instead of lagging half a minute behind it.
  const live = useMemo(() => {
    const counts = { moving: 0, idle: 0, parked: 0, no_signal: 0, unassigned: 0 };
    for (const vehicle of vehicles) {
      const key = !vehicle.device_id
        ? 'unassigned'
        : vehicle.online === false
          ? 'no_signal'
          : vehicle.vehicle_state || 'parked';
      counts[key] = (counts[key] || 0) + 1;
    }
    return counts;
  }, [vehicles]);

  const data = summary.data;

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
      <StatTile label="Vehicles" value={vehicles.length} icon="truck" tone="brand" />
      <StatTile label="Moving now" value={live.moving} icon="arrow" tone="good" />
      <StatTile
        label="No signal"
        value={live.no_signal}
        icon="slash"
        tone={live.no_signal > 0 ? 'critical' : 'brand'}
      />
      <StatTile
        label="Open alerts"
        value={data?.alerts?.open ?? '—'}
        icon="bell"
        tone={data?.alerts?.critical > 0 ? 'critical' : 'warning'}
      />
      <StatTile
        label="Distance today"
        value={data?.today?.distance_km !== undefined ? Number(data.today.distance_km) : '—'}
        unit="km"
        icon="route"
        tone="accent"
      />
    </div>
  );
}

function FleetStatusCard({ summary, vehicles }) {
  const segments = useMemo(() => {
    const counts = {};
    for (const vehicle of vehicles) {
      const key = !vehicle.device_id
        ? 'unassigned'
        : vehicle.online === false
          ? 'no_signal'
          : vehicle.vehicle_state || 'parked';
      counts[key] = (counts[key] || 0) + 1;
    }
    return VEHICLE_STATE_ORDER.map((key) => ({
      key,
      label: VEHICLE_STATES[key].label,
      tone: VEHICLE_STATES[key].tone,
      value: counts[key] || 0,
    }));
  }, [vehicles]);

  const today = summary.data?.today;

  return (
    <Card>
      <CardHeader title="Fleet status" subtitle="Right now" icon="dashboard" />
      <div className="p-4">
        <StackedBar segments={segments} height={12} />

        <dl className="mt-4 grid grid-cols-3 gap-3 border-t border-line pt-3">
          <Metric label="Trips today" value={today ? fmt.number(today.trips) : '—'} />
          <Metric label="Average speed" value={today ? fmt.speed(Number(today.avg_speed)) : '—'} />
          <Metric
            label="Average health"
            value={summary.data?.vehicles?.avg_health ? Math.round(summary.data.vehicles.avg_health) : '—'}
          />
        </dl>
      </div>
    </Card>
  );
}

function Metric({ label, value }) {
  return (
    <div>
      <dt className="text-[11.5px] text-ink-3">{label}</dt>
      <dd className="mt-0.5 text-[16px] font-semibold text-ink tabular-nums">{value}</dd>
    </div>
  );
}

function AlertsCard({ alerts }) {
  const navigate = useNavigate();

  return (
    <Card className="overflow-hidden">
      <CardHeader
        title="Open alerts"
        icon="bell"
        actions={
          <Button variant="ghost" size="sm" iconRight="chevronRight" onClick={() => navigate('/alerts')}>
            All
          </Button>
        }
      />
      <AsyncBoundary
        state={alerts}
        skeleton={<Skeleton className="m-4 h-32" />}
        isEmpty={(data) => !data.alerts.length}
        empty={
          <EmptyState
            icon="check"
            title="Nothing needs attention"
            description="No open alerts across the vehicles you can see."
          />
        }
      >
        {(data) => (
          <ul className="divide-y divide-line">
            {data.alerts.map((alert) => (
              <AlertItem key={alert.id} alert={alert} compact />
            ))}
          </ul>
        )}
      </AsyncBoundary>
    </Card>
  );
}

function HealthCard({ health }) {
  return (
    <Card>
      <CardHeader title="Device health" subtitle="Across the devices you can see" icon="chip" />
      <div className="p-4">
        <AsyncBoundary state={health} skeleton={<Skeleton className="h-40" />}>
          {(data) => {
            const bands = (data.bands || []).map((row) => ({
              key: row.band,
              label: HEALTH_BANDS[row.band]?.label || row.band,
              tone: HEALTH_BANDS[row.band]?.tone || 'neutral',
              value: Number(row.devices),
            }));

            const flags = (data.flags || []).slice(0, 5).map((row) => ({
              key: row.flag,
              label: healthFlagLabel(row.flag),
              value: Number(row.devices),
              tone: 'warning',
            }));

            return (
              <div className="space-y-5">
                <StackedBar segments={bands} height={12} />

                {flags.length > 0 && (
                  <div>
                    <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-3">
                      What is dragging scores down
                    </p>
                    <BarList items={flags} />
                  </div>
                )}

                {(data.firmware || []).length > 1 && (
                  <div>
                    <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-3">
                      Firmware in the field
                    </p>
                    <BarList
                      items={(data.firmware || []).slice(0, 4).map((row) => ({
                        key: row.firmware_version || 'unknown',
                        label: row.firmware_version || 'Unknown',
                        value: Number(row.devices),
                      }))}
                    />
                  </div>
                )}
              </div>
            );
          }}
        </AsyncBoundary>
      </div>
    </Card>
  );
}

/* ----------------------------------------------------------------- utils -- */

const FLAG_LABELS = {
  connectivity: 'Connectivity',
  fix: 'GNSS fix quality',
  signal: 'Cellular signal',
  continuity: 'Packet continuity',
  memory: 'Device memory',
  firmware: 'Firmware version',
  tamper: 'Tamper / tilt',
};

function healthFlagLabel(flag) {
  return FLAG_LABELS[flag] || flag;
}

/**
 * Triage order: offline first, then worst health, then oldest packet. This is
 * the list someone opens the dashboard to look at, so it is sorted by what is
 * wrong rather than alphabetically.
 */
function triageOrder(vehicles) {
  return [...vehicles].sort((a, b) => {
    const aOffline = a.device_id && a.online === false ? 0 : 1;
    const bOffline = b.device_id && b.online === false ? 0 : 1;
    if (aOffline !== bOffline) return aOffline - bOffline;

    const aHealth = a.health_score ?? 999;
    const bHealth = b.health_score ?? 999;
    if (aHealth !== bHealth) return aHealth - bHealth;

    return new Date(a.last_packet_at || 0) - new Date(b.last_packet_at || 0);
  });
}

function partOfDay() {
  const hour = new Date().getHours();
  if (hour < 12) return 'morning';
  if (hour < 18) return 'afternoon';
  return 'evening';
}
